import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {CLStore} from 'common/src/redux/store';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {LocationCardScratchpadState} from '../../scratchpad/locationCardScratchpad.types';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import getOrCreateLocationCard from '../../utils/location/getOrCreateLocationCard';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import askAboutObjectsTool from '../../../../tools/general/askAboutObjectsTool/askAboutObjectsTool';
import setLocationFeedTool from './setLocationFeedTool';
import setLocationSetTool from './setLocationSetTool';
import setLocationTargetingMethodTool from './setLocationTargetingMethodTool';
import setMaxDistanceTool from './setMaxDistanceTool';
import setTopLocationsTool from './setTopLocationsTool';

const cardSpecificInstructions = `### What is a Location card
A Location card selects a single location from a location feed to inject into the ad context. Different targeting methods determine how the location is selected (nearest, by zip code, by DMA, or specific store). The fallback output is selected when the strategy fails to select a location.

### Location card rules
- The selected location set must belong to the selected location feed.

### Property Dependencies
- The location feed must be set before: the location set (which must belong to the feed) and specific top locations (validated against the feed).
- The targeting method determines which properties are relevant:
  - Nearest Location: requires max distance configuration, which depends on the targeting method being set.
  - Specific Store: requires top location IDs, which depend on both the targeting method and the feed.
  - By Zip Code / By DMA: no additional property configuration needed beyond the feed.`;

export default function addOrUpdateLocationCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<typeof addOrUpdateCardInputSchema, LocationCardScratchpadState>,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId, card} = await preProcessStandaloneAgent(
      params,
      store,
      getOrCreateLocationCard,
    );

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateLocationCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.STORE]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.STORE,
        cardId: normalizedCardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        getCardDetailsTool(),
        askAboutObjectsTool(store),

        // Location tools
        setLocationFeedTool(),
        setLocationSetTool(),
        setLocationTargetingMethodTool(),
        setMaxDistanceTool(),
        setTopLocationsTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId: normalizedCardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateLocationCard,
    displayName: 'Working on location card',
    description:
      'Add or update a location card. The card selects a single location from location feeds to inject into ad context for creative display or decisioning. ' +
      'Can configure location selection strategy (targeting method), assign a feed and location set, ' +
      'configure max distance and fallback options for proximity-based targeting, or assign specific store location IDs for specific store targeting.',
    execute,
    inputSchema: addOrUpdateCardInputSchema,
  });
}
